import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Asset } from "../types/database";

interface EditAssetFormProps {
  asset: Asset;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function EditAssetForm({
  asset,
  onSuccess,
  onCancel,
}: EditAssetFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    barcode: asset.barcode,
    serial_number: asset.serial_number || "",
    status: asset.status,
    purchase_date: asset.purchase_date
      ? new Date(asset.purchase_date).toISOString().split("T")[0]
      : "",
    purchase_cost: asset.purchase_cost?.toString() || "",
    supplier: asset.supplier || "",
    order_number: asset.order_number || "",
    warranty_end: asset.warranty_end
      ? new Date(asset.warranty_end).toISOString().split("T")[0]
      : "",
    notes: asset.notes || "",
  });

  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || !isNaN(Number(value))) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const updateAssetMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      // Use asset.organization_id if available, otherwise use currentOrganization.id
      const organizationId = asset.organization_id || currentOrganization.id;

      try {
        console.log(
          `Updating asset ${asset.id} for organization: ${organizationId}`
        );

        return await inventoryService.updateAsset(
          asset.id,
          {
            barcode: formData.barcode,
            serial_number: formData.serial_number || null,
            status: formData.status as
              | "available"
              | "checked_out"
              | "maintenance"
              | "retired",
            purchase_date: formData.purchase_date
              ? new Date(formData.purchase_date).toISOString()
              : null,
            purchase_cost: formData.purchase_cost
              ? parseFloat(formData.purchase_cost)
              : null,
            supplier: formData.supplier || null,
            order_number: formData.order_number || null,
            warranty_end: formData.warranty_end
              ? new Date(formData.warranty_end).toISOString()
              : null,
            notes: formData.notes || null,
            updated_by: user.id,
            // Ensure organization_id is set if it wasn't before
            organization_id: organizationId,
          },
          organizationId
        );
      } catch (error: any) {
        setError(error.message || "Failed to update asset");
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["asset", asset.id] });
      if (onSuccess) onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.barcode) {
      setError("Barcode is required");
      return;
    }

    updateAssetMutation.mutate();
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Edit Asset</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Barcode */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Barcode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="barcode"
              value={formData.barcode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Serial Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Serial Number
            </label>
            <input
              type="text"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="available">Available</option>
              <option value="checked_out">Checked Out</option>
              <option value="maintenance">Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          {/* Purchase Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date
            </label>
            <input
              type="date"
              name="purchase_date"
              value={formData.purchase_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Purchase Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Cost
            </label>
            <input
              type="text"
              name="purchase_cost"
              value={formData.purchase_cost}
              onChange={handleNumberChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supplier
            </label>
            <input
              type="text"
              name="supplier"
              value={formData.supplier}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Order Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Number
            </label>
            <input
              type="text"
              name="order_number"
              value={formData.order_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Warranty End */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Warranty End Date
            </label>
            <input
              type="date"
              name="warranty_end"
              value={formData.warranty_end}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            disabled={updateAssetMutation.isPending}
          >
            {updateAssetMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
